import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const PresentationModeContext = createContext();

export const PresentationModeProvider = ({ children }) => {
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const navigationCallbacksRef = useRef({ onNext: null, onPrevious: null });

  const togglePresentationMode = useCallback(() => {
    setIsPresentationMode(prev => !prev);
  }, []);

  const enterPresentationMode = useCallback(() => {
    setIsPresentationMode(true);
  }, []);

  const exitPresentationMode = useCallback(() => {
    setIsPresentationMode(false);
  }, []);

  // Register navigation callbacks from the page
  const registerNavigationCallbacks = useCallback((onNext, onPrevious) => {
    navigationCallbacksRef.current = { onNext, onPrevious };
  }, []);

  // Unregister navigation callbacks
  const unregisterNavigationCallbacks = useCallback(() => {
    navigationCallbacksRef.current = { onNext: null, onPrevious: null };
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Escape to exit presentation mode
      if (e.key === 'Escape' && isPresentationMode) {
        exitPresentationMode();
      }
      
      // F11 to toggle presentation mode
      if (e.key === 'F11') {
        e.preventDefault();
        togglePresentationMode();
      }
      
      // Arrow keys for navigation in presentation mode
      if (isPresentationMode) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          if (navigationCallbacksRef.current.onNext) {
            navigationCallbacksRef.current.onNext();
          }
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          if (navigationCallbacksRef.current.onPrevious) {
            navigationCallbacksRef.current.onPrevious();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPresentationMode, togglePresentationMode, exitPresentationMode]);

  return (
    <PresentationModeContext.Provider value={{
      isPresentationMode,
      togglePresentationMode,
      enterPresentationMode,
      exitPresentationMode,
      registerNavigationCallbacks,
      unregisterNavigationCallbacks
    }}>
      {children}
    </PresentationModeContext.Provider>
  );
};

export const usePresentationMode = () => {
  const context = useContext(PresentationModeContext);
  if (!context) {
    throw new Error('usePresentationMode must be used within a PresentationModeProvider');
  }
  return context;
};
